import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { Title, Fieldset, Button, Control, Field } from "rbx";
import { useMutation, useApolloClient } from "react-apollo";

import {
  CREATE_DRUG_TEST_CATEGORY_MUTATION,
  UPDATE_DRUG_TEST_CATEGORY_MUTATION,
  DELETE_DRUG_TEST_CATEGORY_MUTATION
} from "../../graphql/DrugTests/DrugTestCategories.graphql";
import { ALL_DRUG_TESTS_QUERY } from "../../graphql/DrugTests/DrugTests.graphql";
import { ourToast as toast } from "../../utils";
import DrugTestCategoryForm from "./DrugTestCategoryForm";
import { ModalContext } from "../ModalProvider";
import { Confirmation } from "../modals";

const DEFAULT_STATE = {
  TestCode: "",
  Description: "",
  Status: true,
  AlcoholTest: false,
  DrugTest: false
};

const AddDrugTestCategoryForm = ({ onComplete, data }) => {
  const [inputs, setInputs] = useState({ ...DEFAULT_STATE });
  const [editing, setEditing] = useState(false);
  const client = useApolloClient();
  const { setModalOpen } = useContext(ModalContext);

  useEffect(() => {
    if (data && data.RecId) {
      setInputs({
        TestCode: data.TestCode,
        Description: data.Description,
        Status: data.Status,
        AlcoholTest: data.AlcoholTest,
        DrugTest: data.DrugTest
      });
      setEditing(true);
    }
  }, [data]);

  const handleChange = e => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setInputs({
      ...inputs,
      [e.target.name]: value
    });
  };

  const [createDrugTestCategory, { loading }] = useMutation(
    CREATE_DRUG_TEST_CATEGORY_MUTATION,
    {
      refetchQueries: [
        "ALL_DRUG_TEST_CATEGORIES_QUERY",
        "SINGLE_DRUG_TEST_CATEGORY_QUERY"
      ]
    }
  );
  const [updateDrugTestCategory, res] = useMutation(
    UPDATE_DRUG_TEST_CATEGORY_MUTATION,
    {
      refetchQueries: [
        "ALL_DRUG_TEST_CATEGORIES_QUERY",
        "SINGLE_DRUG_TEST_CATEGORY_QUERY"
      ]
    }
  );

  const handleSave = async e => {
    e.preventDefault();
    try {
      if (data && data.RecId) {
        await updateDrugTestCategory({
          variables: {
            data: {
              TestCode: inputs.TestCode,
              Description: inputs.Description,
              Status: inputs.Status,
              AlcoholTest: inputs.AlcoholTest,
              DrugTest: inputs.DrugTest
            },
            where: { RecId: parseInt(data.RecId) }
          }
        });
        toast.success("Drug Test edited successfully.");
      } else {
        await createDrugTestCategory({
          variables: {
            data: {
              ...inputs
            }
          }
        });
        toast.success("Drug Test created successfully.");
      }
      onComplete();
    } catch (err) {
      console.log(err.message);
    }
  };

  const handleDelete = async e => {
    e.preventDefault();
    try {
      const {
        data: { DrugTests }
      } = await client.query({
        query: ALL_DRUG_TESTS_QUERY,
        variables: {
          where: {
            TestCategoryRecIdRef: parseInt(data.RecId)
          }
        },
        fetchPolicy: "network-only"
      });
      if (DrugTests?.length > 0) {
        toast.warning(
          "The Category is on a Drug Test record and may be made inactive only."
        );
        setInputs({
          ...inputs,
          Status: false
        });
      } else {
        const performDelete = async () => {
          setModalOpen(false, "");
          await client.mutate({
            mutation: DELETE_DRUG_TEST_CATEGORY_MUTATION,
            variables: {
              where: {
                RecId: parseInt(data.RecId)
              }
            },
            refetchQueries: [
              "ALL_DRUG_TEST_CATEGORIES_QUERY",
              "SINGLE_DRUG_TEST_CATEGORY_QUERY"
            ]
          });
          toast.success("Drug Test deleted successfully.");
        };
        setModalOpen(
          true,
          <Confirmation
            message="Are you sure you want to delete?"
            onConfirm={performDelete}
          />
        );
      }
    } catch (err) {
      console.log(err.message);
    }
  };

  return (
    <form
      className="add-item-form"
      id="add-item-form"
      style={{ width: 600, maxWidth: "100%" }}
      onSubmit={handleSave}
    >
      <Title>{data?.RecId ? "Edit" : "Create"} Drug Test Category</Title>
      <Fieldset disabled={loading || res.loading}>
        <DrugTestCategoryForm
          editing={editing}
          inputs={inputs}
          onChange={handleChange}
        />
        <hr />
        <Field kind="group">
          <Control expanded>
            <Field kind="group">
              <Control>
                <Button color="success" form="add-item-form" type="submit">
                  Save
                </Button>
              </Control>
              <Control>
                <Button type="button" onClick={onComplete}>
                  Cancel
                </Button>
              </Control>
            </Field>
          </Control>
          {data?.RecId && (
            <Control>
              <Button color="danger" onClick={handleDelete}>
                Delete
              </Button>
            </Control>
          )}
        </Field>
      </Fieldset>
    </form>
  );
};

AddDrugTestCategoryForm.propTypes = {
  onComplete: PropTypes.func,
  data: PropTypes.object
};
export default AddDrugTestCategoryForm;
